import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, patch, toastError } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAuth } from "@/context/AuthContext";
import { ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ModuleTypeSelect, EnumSelect } from "@/components/common/Pickers";
import { Gantt, GanttSkeleton, ZOOM, ZOOM_ORDER, LEGEND, Swatch } from "@/components/planning/Gantt";
import { CapacityView, CapacitySkeleton } from "@/components/planning/CapacityView";
import { ConflictRail } from "@/components/planning/ConflictRail";
import { AllocationSheet } from "@/components/planning/AllocationSheet";
import { AllocateSheet, ImpactSheet } from "@/components/planning/PlanningSheets";
import { fmtDate, todayISO, addDaysISO } from "@/lib/format";
import { MODULE_STATUSES, label } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Check, ChevronDown, ChevronLeft, ChevronRight, ChevronsDownUp, ChevronsUpDown, GanttChart, MoreHorizontal, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { differenceInCalendarDays, parseISO, startOfYear, endOfYear, subYears, format } from "date-fns";

const VIEWS = [
  { value: "project", label: "Prosjekter", testId: "planning-view-project" },
  { value: "module", label: "Moduler", testId: "planning-view-module" },
  { value: "capacity", label: "Kapasitet", testId: "planning-view-capacity" },
];
const SHORTCUTS = [
  ["T", "Gå til i dag"],
  ["[  /  ]", "Bla én skjermbredde bakover / fremover i tid"],
  ["1 – 4", "Zoom: dag, uke, måned, kvartal"],
  ["V", "Bytt visning (prosjekter → moduler → kapasitet)"],
  ["P", "Velg prosjekt"],
  ["/", "Søk"],
  ["F", "Åpne filter"],
  ["C", "Vis kun konflikter"],
  ["K", "Vis/skjul konfliktpanel"],
  ["N", "Ny reservasjon"],
  ["← / →", "Flytt valgt allokering 1 dag (⇧ = 7 dager)"],
  ["Enter", "Åpne valgt element"],
  ["Esc", "Fjern valg / lukk"],
  ["?", "Vis denne oversikten"],
];

/* ---------------- building blocks ---------------- */
/** Text-first segmented control. */
const Segmented = ({ value, onChange, options, testId, size = "md", className }) => (
  <div role="tablist" className={cn("inline-flex items-center rounded-md bg-muted/70 p-0.5", className)} data-testid={testId}>
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-[5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            size === "sm" ? "h-6 px-2 text-[11.5px]" : "h-7 px-2.5 text-[12.5px]",
            active ? "bg-background text-foreground shadow-[0_1px_2px_rgba(16,24,40,0.08)]" : "text-muted-foreground hover:text-foreground"
          )}
          data-testid={o.testId}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

const TextBtn = ({ children, onClick, active, testId, className, tone, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium text-foreground/80 transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      active && "bg-muted text-foreground",
      tone === "danger" && "text-rose-700 hover:bg-rose-500/10 hover:text-rose-800 dark:text-rose-300",
      className
    )}
    data-testid={testId}
    {...rest}
  >
    {children}
  </button>
);

const IconBtn = ({ icon: Icon, label: l, onClick, testId, className, active }) => (
  <button type="button" onClick={onClick} aria-label={l} title={l} aria-pressed={active} className={cn("inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring", active && "bg-muted text-foreground", className)} data-testid={testId}>
    <Icon className="h-4 w-4" />
  </button>
);

const ProjectPicker = ({ value, onChange, open, onOpenChange, compact }) => {
  const { data } = useQuery({ queryKey: ["projects"], queryFn: () => get("/projects") });
  const list = useMemo(() => (data || []).slice().sort((a, b) => (a.start_date < b.start_date ? 1 : -1)), [data]);
  const current = list.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={cn("inline-flex h-8 min-w-0 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring", value ? "max-w-[52vw] sm:max-w-[320px]" : "")} data-testid="planning-project-picker">
          {!compact || value ? <span className="truncate">{current ? current.name : "Alle prosjekter"}</span> : <span className="truncate">Alle</span>}
          {current && <span className="hidden font-mono text-[11px] font-normal text-muted-foreground lg:inline">{current.project_number}</span>}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] max-w-[94vw] p-0" align="start">
        <Command>
          <CommandInput placeholder="Søk prosjekt, nummer eller kunde…" data-testid="planning-project-search" />
          <CommandList className="max-h-[380px]">
            <CommandEmpty>Ingen prosjekter funnet.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all" onSelect={() => { onChange(""); onOpenChange(false); }} data-testid="planning-project-option-all">
                <span className="flex-1">Alle prosjekter</span>{!value && <Check className="h-4 w-4 text-primary" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Kalender for ett prosjekt">
              {list.map((p) => (
                <CommandItem key={p.id} value={`${p.project_number} ${p.name} ${p.customer_name || ""}`} onSelect={() => { onChange(p.id, p); onOpenChange(false); }} data-testid={`planning-project-option-${p.project_number}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="truncate text-[13px]">{p.name}</span><span className="font-mono text-[11px] text-muted-foreground">{p.project_number}</span></div>
                    <div className="text-[11px] text-muted-foreground tabular">{p.customer_name ? `${p.customer_name} · ` : ""}{fmtDate(p.start_date, "d. MMM yy")} – {fmtDate(p.end_date, "d. MMM yy")} · {label(p.status)}</div>
                  </div>
                  {value === p.id && <Check className="ml-2 h-4 w-4 text-primary" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** Shows the dates currently visible in the timeline; the popover jumps to a period (the timeline itself extends endlessly when scrolled). */
const PeriodControl = ({ visible, range, onJump, focus }) => {
  const today = todayISO();
  const presets = [
    { l: "I dag", s: addDaysISO(today, -14), e: addDaysISO(today, 90), to: today },
    { l: "3 mnd", s: addDaysISO(today, -14), e: addDaysISO(today, 90) },
    { l: "6 mnd", s: addDaysISO(today, -30), e: addDaysISO(today, 180) },
    { l: "12 mnd", s: addDaysISO(today, -30), e: addDaysISO(today, 365) },
    { l: "Forrige år", s: format(startOfYear(subYears(new Date(), 1)), "yyyy-MM-dd"), e: format(endOfYear(subYears(new Date(), 1)), "yyyy-MM-dd") },
    { l: "Dette året", s: format(startOfYear(new Date()), "yyyy-MM-dd"), e: format(endOfYear(new Date()), "yyyy-MM-dd") },
    ...(focus ? [{ l: "Prosjektperioden", s: addDaysISO(focus.start_date, -14), e: addDaysISO(focus.end_date, 14) }] : []),
  ];
  const shown = visible || range;
  const [draft, setDraft] = useState({ s: shown.start, e: shown.end });
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open) setDraft({ s: shown.start, e: shown.end }); }, [open]);
  const sameYear = shown.start.slice(0, 4) === shown.end.slice(0, 4);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12.5px] font-medium tabular text-foreground/90 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="planning-period-button" title="Synlig periode – klikk for å gå til en annen periode">
          <span data-testid="planning-visible-range">{fmtDate(shown.start, sameYear ? "d. MMM" : "d. MMM yy")} – {fmtDate(shown.end, "d. MMM yyyy")}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-3 space-y-3" align="center" data-testid="planning-period-popover">
        <div className="text-[11.5px] text-muted-foreground">Tidslinjen fortsetter når du blar. Hopp til:</div>
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => <Button key={p.l} size="sm" variant="outline" className="h-7 text-xs shadow-none" onClick={() => { onJump(p.s, p.e, p.to || p.s); setOpen(false); }} data-testid={`planning-period-preset-${p.l.replace(/\s/g, "-").toLowerCase()}`}>{p.l}</Button>)}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[11px]">Fra</Label><Input type="date" className="h-8" value={draft.s} onChange={(e) => setDraft({ ...draft, s: e.target.value })} data-testid="planning-start-input" /></div>
          <div><Label className="text-[11px]">Til</Label><Input type="date" className="h-8" value={draft.e} onChange={(e) => setDraft({ ...draft, e: e.target.value })} data-testid="planning-end-input" /></div>
        </div>
        <Button size="sm" className="w-full h-8 shadow-none" disabled={!draft.s || !draft.e || draft.e <= draft.s} onClick={() => { onJump(draft.s, draft.e, draft.s); setOpen(false); }} data-testid="planning-period-apply">Gå til periode</Button>
      </PopoverContent>
    </Popover>
  );
};

const Stat = ({ label: l, value, tone, onClick, testId }) => {
  const Comp = onClick ? "button" : "span";
  return (
    <Comp type={onClick ? "button" : undefined} onClick={onClick} className={cn("inline-flex shrink-0 items-baseline gap-1 whitespace-nowrap rounded px-1 text-[11.5px] text-muted-foreground", onClick && "hover:bg-muted hover:text-foreground", tone === "danger" && "text-rose-700 dark:text-rose-300")} data-testid={testId}>
      <span className={cn("font-mono tabular text-foreground/90", tone === "danger" && "text-rose-700 dark:text-rose-300 font-semibold")}>{value}</span>
      <span>{l}</span>
    </Comp>
  );
};

/* ---------------- page ---------------- */
export default function PlanningPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canWrite = can("planning:write");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMd = useMediaQuery("(min-width: 768px)");
  const today = todayISO();
  const defaults = useMemo(() => ({ view: "project", zoom: "week", module_type_id: "", status: "", q: "", project_id: "", conflicts: "", strip: "1" }), []);
  const { filters, setFilter, setMany, reset: resetFilters, active } = useFilters("planning", defaults);
  const view = ["project", "module", "capacity"].includes(filters.view) ? filters.view : "project";
  const zoom = ZOOM[filters.zoom] ? filters.zoom : "week";
  const focusId = filters.project_id || "";
  const showStrip = filters.strip !== "0";
  const tlView = view === "capacity" ? "project" : view;

  /* Loaded time window. Not persisted: the Gantt extends it on demand while the user scrolls backwards/forwards in time. */
  const defaultRange = useMemo(() => ({ start: addDaysISO(today, -90), end: addDaysISO(today, 270) }), [today]);
  const [range, setRangeState] = useState(defaultRange);
  const setRange = useCallback((patch) => setRangeState((r) => ({ ...r, ...patch })), []);
  const [visible, setVisible] = useState(null);
  const onVisibleRange = useCallback((r) => setVisible(r), []);

  const params = { view: tlView, start: range.start, end: range.end, module_type_id: filters.module_type_id || undefined, status: filters.status || undefined, q: filters.q || undefined, project_id: focusId || undefined };
  const { data, isLoading, isFetching, error, refetch } = useQuery({ queryKey: ["planning", params], queryFn: () => get("/planning/timeline", params), placeholderData: (prev) => prev });
  const capParams = { start: range.start, end: range.end, bucket: "week", module_type_id: view !== "project" && filters.module_type_id ? filters.module_type_id : undefined };
  const { data: capacity, isLoading: capLoading, error: capError, refetch: refetchCap } = useQuery({ queryKey: ["capacity", capParams], queryFn: () => get("/planning/capacity", capParams), placeholderData: (prev) => prev });

  const ganttRef = useRef(null);
  const searchRef = useRef(null);
  const pendingScroll = useRef(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({ barId: null, rowId: null });
  const [allocId, setAllocId] = useState(null);
  const [impact, setImpact] = useState(null);
  const [allocate, setAllocate] = useState(null);
  const [dragConflict, setDragConflict] = useState(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem("planning:rail") !== "0");
  const [railSheet, setRailSheet] = useState(false);
  useEffect(() => localStorage.setItem("planning:rail", railOpen ? "1" : "0"), [railOpen]);

  const focus = data?.focus || null;
  const conflicts = data?.conflicts || [];
  const conflictCount = data?.conflict_count ?? 0;

  /* rows */
  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (filters.conflicts) r = r.filter((row) => row.has_conflict || (row.children || []).some((c) => c.has_conflict));
    return r;
  }, [data, filters.conflicts]);

  useEffect(() => {
    if (focusId && tlView === "project" && rows[0] && !expanded[rows[0].id]) setExpanded((e) => ({ ...e, [rows[0].id]: true }));
  }, [focusId, tlView, rows]);

  const flat = useMemo(() => {
    const out = [];
    if (focus && tlView === "module") {
      out.push({ id: `focus-${focus.id}`, kind: "project", synthetic: true, depth: 0, label: focus.name, sublabel: `${focus.project_number} · ${focus.customer_name || ""}`, module_count: focus.module_count, has_conflict: focus.conflict_count > 0, status: focus.status, project_id: focus.id,
        bars: [{ id: focus.id, kind: "project", type: "prosjekt", status: focus.status, start: focus.start_date, end: focus.end_date, label: focus.name, project_id: focus.id, project_number: focus.project_number, conflict: false }] });
      if (focus.transports?.length) out.push({ id: `focus-tr-${focus.id}`, kind: "group", depth: 0, label: "Transport", sublabel: `${focus.transports.length} transport${focus.transports.length === 1 ? "" : "er"}`, bars: focus.transports });
      if (focus.tasks?.length) out.push({ id: `focus-tk-${focus.id}`, kind: "group", depth: 0, label: "Oppgaver", sublabel: `${focus.tasks.length} oppgave${focus.tasks.length === 1 ? "" : "r"}`, bars: focus.tasks });
    }
    for (const r of rows) {
      const expandable = tlView === "project" && (r.children?.length || 0) > 0;
      out.push({ ...r, depth: 0, expandable, expanded: !!expanded[r.id] });
      if (expandable && expanded[r.id]) for (const c of r.children) out.push({ ...c, depth: 1, parent: r });
    }
    return out;
  }, [rows, expanded, tlView, focus]);

  useEffect(() => {
    if (pendingScroll.current && data && !isFetching) {
      const d = pendingScroll.current;
      pendingScroll.current = null;
      setTimeout(() => ganttRef.current?.scrollToDate(d, "smooth", { reveal: true }), 50);
    }
  }, [data, isFetching]);

  /* KPIs */
  const capNow = useMemo(() => {
    if (!capacity) return null;
    const i = capacity.buckets.findIndex((b) => b.start <= today && today < b.end);
    return i >= 0 ? capacity.totals[i] : null;
  }, [capacity, today]);
  const stripTotals = useMemo(() => (capacity && showStrip ? capacity.totals.map((t, i) => ({ ...t, ...capacity.buckets[i] })) : null), [capacity, showStrip]);

  /* actions */
  const invalidateAll = useCallback(async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ["planning"] }), qc.invalidateQueries({ queryKey: ["capacity"] })]);
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["modules"] });
  }, [qc]);

  const reset = useCallback(() => { resetFilters(); setRangeState(defaultRange); pendingScroll.current = today; ganttRef.current?.scrollToToday(); }, [resetFilters, defaultRange, today]);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  /** Jump to a period: widen the loaded window (with margin, so the target never sits at an edge), then scroll to `to` – immediately, or once the new data is in. */
  const jumpTo = useCallback((s, e, to) => {
    const r = rangeRef.current;
    const margin = Math.ceil(800 / (ZOOM[zoomRef.current]?.px || ZOOM.week.px));
    const ns = addDaysISO(s, -margin) < r.start ? addDaysISO(s, -margin) : r.start;
    const ne = addDaysISO(e, margin) > r.end ? addDaysISO(e, margin) : r.end;
    if (ns === r.start && ne === r.end) { ganttRef.current?.scrollToDate(to || s, "smooth", { reveal: true }); return; }
    setRangeState({ start: ns, end: ne });
    pendingScroll.current = to || s;
  }, []);
  const page = (dir) => (view === "capacity" ? setRange({ start: addDaysISO(range.start, dir * 30), end: addDaysISO(range.end, dir * 30) }) : ganttRef.current?.scrollByPage(dir));
  const setZoom = (z) => setFilter("zoom", z);
  const setView = (v) => {
    if (v === "capacity") { navigate("/planlegging/kapasitet"); return; } // Kapasitet is its own control room (Planlegging → Kapasitet)
    setMany({ view: v, conflicts: filters.conflicts });
  };
  const toggleRow = useCallback((id) => setExpanded((e) => ({ ...e, [id]: !e[id] })), []);
  const expandAll = useCallback((on) => setExpanded(on ? Object.fromEntries(rows.map((r) => [r.id, true])) : {}), [rows]);
  const setFocus = useCallback((id, p) => {
    if (!id) { setMany({ project_id: "" }); return; }
    setMany({ project_id: id, view: "module", conflicts: "", status: "" });
    if (p?.start_date && p?.end_date) jumpTo(addDaysISO(p.start_date, -14), addDaysISO(p.end_date, 14), p.start_date <= today && today <= p.end_date ? today : p.start_date);
  }, [setMany, jumpTo, today]);

  const openBar = useCallback((bar, row) => {
    if (!bar) {
      if (row?.kind === "module") navigate(`/moduler/${row.id}`);
      else if (row?.kind === "project") navigate(`/prosjekter/${row.project_id || row.id}`);
      return;
    }
    if (bar.kind === "allocation") setAllocId(bar.id);
    else if (bar.kind === "project") navigate(`/prosjekter/${bar.project_id}`);
    else if (bar.kind === "transport") navigate(`/prosjekter/${bar.project_id}?tab=transport`);
    else if (bar.kind === "task") navigate(`/prosjekter/${bar.project_id}?tab=oppgaver`);
    else if (bar.kind === "service") navigate(`/service?tab=service`);
  }, [navigate]);

  const onSelect = useCallback((bar, row) => setSelected({ barId: bar?.id || null, rowId: row?.id || null }), []);
  const onRowClick = useCallback((row) => {
    if (row.kind === "allocation") { setAllocId(row.id); setSelected({ barId: row.id, rowId: row.id }); return; }
    setSelected((s) => ({ barId: null, rowId: s.rowId === row.id ? null : row.id }));
  }, []);

  const onBarChange = useCallback(async (bar, row, next) => {
    if (bar.kind === "project") {
      setImpact({ project: { id: bar.project_id, name: bar.label, start_date: bar.start, end_date: bar.end }, initialStart: next.start, initialEnd: next.end });
      return;
    }
    if (bar.kind !== "allocation") return;
    try {
      await patch(`/allocations/${bar.id}`, { start_date: next.start, end_date: next.end });
      toast.success(`${bar.module_code || "Allokering"} flyttet til ${fmtDate(next.start, "d. MMM")} – ${fmtDate(next.end, "d. MMM")}`);
      await invalidateAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setDragConflict({ bar, next, conflicts: d.conflicts, message: d.message });
      else toastError(e);
    }
  }, [invalidateAll]);

  const forceDrag = async () => {
    const { bar, next } = dragConflict;
    try {
      await patch(`/allocations/${bar.id}`, { start_date: next.start, end_date: next.end, force: true });
      toast.warning(`${bar.module_code || "Allokering"} flyttet – konflikt registrert`);
      setDragConflict(null);
      await invalidateAll();
    } catch (e) { toastError(e); }
  };

  const onCreate = useCallback((row, range) => {
    if (row.kind === "module") setAllocate({ projectId: focusId || undefined, preset: { module_id: row.id, module_code: row.label, start_date: range.start, end_date: range.end } });
    else if (row.kind === "project") setAllocate({ projectId: row.project_id || row.id, preset: { start_date: range.start, end_date: range.end } });
  }, [focusId]);

  const locateConflict = (c) => {
    setMany({ view: "module", q: c.module_code, project_id: "", conflicts: "" });
    setSelected({ barId: c.b.id, rowId: c.module_id });
    pendingScroll.current = c.overlap_start;
    if (!isDesktop) setRailSheet(false);
  };
  const resolveConflict = (alloc) => { setAllocId(alloc.id); setSelected({ barId: alloc.id, rowId: alloc.module_id }); if (!isDesktop) setRailSheet(false); };
  const openConflicts = () => (isDesktop ? setRailOpen((o) => !o) : setRailSheet(true));

  /* keyboard shortcuts */
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (allocId || impact || allocate || dragConflict || shortcutsOpen || pickerOpen || filterOpen || document.querySelector("[role='dialog'][data-state='open']")) {
        if (e.key === "Escape") setSelected({ barId: null, rowId: null });
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      if (k === "t" || k === "T") ganttRef.current?.scrollToToday();
      else if (k === "[") page(-1);
      else if (k === "]") page(1);
      else if (["1", "2", "3", "4"].includes(k)) setZoom(ZOOM_ORDER[Number(k) - 1]);
      else if (k === "v" || k === "V") setView(VIEWS[(VIEWS.findIndex((x) => x.value === view) + 1) % VIEWS.length].value);
      else if (k === "p" || k === "P") setPickerOpen(true);
      else if (k === "/") { e.preventDefault(); setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 30); }
      else if (k === "f" || k === "F") setFilterOpen(true);
      else if (k === "c" || k === "C") setFilter("conflicts", filters.conflicts ? "" : "1");
      else if (k === "k" || k === "K") openConflicts();
      else if ((k === "n" || k === "N") && canWrite) setAllocate({ projectId: focusId || undefined });
      else if (k === "?") setShortcutsOpen(true);
      else if (k === "Escape") { setSelected({ barId: null, rowId: null }); if (!filters.q) setSearchOpen(false); }
      else return;
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }); // re-bound each render to capture latest state

  /* row actions menu */
  const renderRowActions = useCallback((row) => {
    if (row.kind === "group") return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring" aria-label="Handlinger" data-testid={`planning-row-menu-${row.id}`}><MoreHorizontal className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          {row.kind === "project" && (
            <>
              <DropdownMenuItem onClick={() => navigate(`/prosjekter/${row.project_id || row.id}`)} data-testid={`planning-row-open-${row.id}`}>Åpne prosjekt</DropdownMenuItem>
              {!row.synthetic && <DropdownMenuItem onClick={() => setFocus(row.id, { start_date: row.bars?.[0]?.start, end_date: row.bars?.[0]?.end })} data-testid={`planning-row-focus-${row.id}`}>Vis kalender for prosjektet</DropdownMenuItem>}
              {canWrite && <DropdownMenuSeparator />}
              {canWrite && <DropdownMenuItem onClick={() => setAllocate({ projectId: row.project_id || row.id })} data-testid={`planning-row-allocate-${row.id}`}>Reserver moduler</DropdownMenuItem>}
              {canWrite && <DropdownMenuItem onClick={() => setImpact({ project: { id: row.project_id || row.id, name: row.label, start_date: row.bars?.[0]?.start, end_date: row.bars?.[0]?.end } })} data-testid={`planning-move-${row.id}`}>Flytt prosjekt (konsekvensanalyse)</DropdownMenuItem>}
            </>
          )}
          {row.kind === "module" && (
            <>
              <DropdownMenuItem onClick={() => navigate(`/moduler/${row.id}`)} data-testid={`planning-row-open-${row.id}`}>Åpne modul</DropdownMenuItem>
              {canWrite && <DropdownMenuItem onClick={() => setAllocate({ projectId: focusId || undefined, preset: { module_id: row.id, module_code: row.label, start_date: focus?.start_date || today, end_date: focus?.end_date || addDaysISO(today, 30) } })} data-testid={`planning-row-reserve-${row.id}`}>Ny reservasjon</DropdownMenuItem>}
            </>
          )}
          {row.kind === "allocation" && <DropdownMenuItem onClick={() => setAllocId(row.id)} data-testid={`planning-row-detail-${row.id}`}>Allokeringsdetaljer</DropdownMenuItem>}
          {(row.kind === "transport" || row.kind === "task") && <DropdownMenuItem onClick={() => openBar(row.bars?.[0], row)}>Åpne</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [navigate, setFocus, canWrite, focusId, focus?.start_date, focus?.end_date, today, openBar]);

  const leftHeader = useMemo(() => (
    <div className="relative flex w-full items-center justify-between gap-1">
      <span className="text-[11px] text-muted-foreground tabular" data-testid="planning-row-count">
        {tlView === "project" ? `${rows.length} prosjekt${rows.length === 1 ? "" : "er"}` : `${rows.length} modul${rows.length === 1 ? "" : "er"}`}
        {isFetching && !isLoading ? " · oppdaterer…" : ""}
      </span>
      {tlView === "project" && rows.some((r) => r.children?.length) && (
        <div className="flex items-center">
          <button type="button" onClick={() => expandAll(true)} className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground/70 hover:bg-muted hover:text-foreground" aria-label="Utvid alle" title="Utvid alle" data-testid="planning-expand-all"><ChevronsUpDown className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => expandAll(false)} className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground/70 hover:bg-muted hover:text-foreground" aria-label="Fold alle" title="Fold alle" data-testid="planning-collapse-all"><ChevronsDownUp className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  ), [tlView, rows, isFetching, isLoading, expandAll]);

  const emptyContent = useMemo(() => (
    <EmptyState
      icon={GanttChart}
      title={filters.conflicts ? "Ingen konflikter" : focus ? "Ingen moduler på prosjektet ennå" : "Ingen rader i perioden"}
      description={filters.conflicts ? "Ingen dobbelbookinger med gjeldende filter." : focus ? "Reserver moduler for å bygge opp prosjektets kalender." : "Bla bakover eller fremover i tid, eller juster filter for å se prosjekter og moduler."}
      action={focus && canWrite ? <Button size="sm" className="shadow-none" onClick={() => setAllocate({ projectId: focus.id })} data-testid="planning-empty-allocate"><Plus className="h-4 w-4 mr-1" /> Reserver moduler</Button> : active > 0 ? <Button size="sm" variant="outline" className="shadow-none" onClick={reset}>Nullstill filter</Button> : null}
      testId="planning-empty-state"
    />
  ), [filters.conflicts, focus, canWrite, active, reset]);

  const activeChips = [
    filters.module_type_id && view !== "project" ? { k: "module_type_id", l: "Modultype" } : null,
    filters.status && view === "module" ? { k: "status", l: `Status: ${label(filters.status)}` } : null,
    filters.q ? { k: "q", l: `Søk: «${filters.q}»` } : null,
    filters.conflicts ? { k: "conflicts", l: "Kun konflikter" } : null,
  ].filter(Boolean);
  const filterCount = activeChips.length + (showStrip ? 0 : 1);
  const showRail = isDesktop && railOpen && view !== "capacity";
  const searchVisible = isDesktop || searchOpen || !!filters.q;

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[480px] flex-col bg-card" data-testid="planning-page">
      {/* ---------- command bar ---------- */}
      <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-1 border-b px-2 py-1 sm:h-11 sm:flex-nowrap sm:py-0 sm:px-3" data-testid="planning-toolbar">
        <ProjectPicker value={focusId} onChange={setFocus} open={pickerOpen} onOpenChange={setPickerOpen} compact={!isMd} />
        {focusId && <IconBtn icon={X} label="Vis alle prosjekter" onClick={() => setFocus("")} testId="planning-project-clear" className="-ml-1 h-7 w-7" />}
        <Segmented value={view} onChange={setView} options={VIEWS} testId="planning-view-toggle" className="ml-1" />

        {/* period */}
        <div className="flex items-center sm:mx-auto">
          <IconBtn icon={ChevronLeft} label="Bakover i tid" onClick={() => page(-1)} testId="planning-prev-button" />
          <PeriodControl visible={view === "capacity" ? null : visible} range={range} onJump={jumpTo} focus={focus} />
          <IconBtn icon={ChevronRight} label="Fremover i tid" onClick={() => page(1)} testId="planning-next-button" />
          {view !== "capacity" && <TextBtn onClick={() => ganttRef.current?.scrollToToday()} testId="planning-today-button" className="hidden sm:inline-flex text-muted-foreground">I dag</TextBtn>}
          {view !== "capacity" && isMd && <Segmented size="sm" value={zoom} onChange={setZoom} options={ZOOM_ORDER.map((z) => ({ value: z, label: ZOOM[z].label, testId: `planning-zoom-${z}` }))} testId="planning-zoom-select" className="ml-2" />}
        </div>

        {/* right cluster */}
        <div className="ml-auto flex items-center gap-0.5 sm:ml-0">
          {view !== "capacity" && (
            <div className={cn("relative", searchVisible ? "flex" : "hidden")}>
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input ref={searchRef} placeholder={tlView === "module" ? "Søk modul-ID" : "Søk prosjekt"} value={filters.q || ""} onChange={(e) => setFilter("q", e.target.value)} onBlur={() => { if (!filters.q) setSearchOpen(false); }} className="h-8 w-32 border-transparent bg-transparent pl-7 pr-6 text-[12.5px] shadow-none hover:bg-muted focus-visible:bg-muted focus-visible:ring-0 xl:w-40" data-testid="planning-search-input" />
              {filters.q && <button type="button" onClick={() => setFilter("q", "")} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Tøm søk" data-testid="planning-search-clear"><X className="h-3.5 w-3.5" /></button>}
            </div>
          )}
          {view !== "capacity" && !searchVisible && <IconBtn icon={Search} label="Søk" onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 30); }} testId="planning-search-button" />}

          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <button type="button" aria-label="Filter" className={cn("inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium text-foreground/80 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring", filterCount > 0 && "text-foreground")} data-testid="planning-filter-button">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden lg:inline">Filter</span>
                {filterCount > 0 && <span className="font-mono text-[11px] text-primary tabular">{filterCount}</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[300px] p-3 space-y-3" data-testid="planning-filter-popover">
              <div className="flex items-center justify-between"><div className="text-[13px] font-medium">Filter og visning</div>{active > 0 && <button type="button" className="text-[12px] text-muted-foreground hover:text-foreground" onClick={() => { reset(); setFilterOpen(false); }} data-testid="filters-reset-button">Nullstill</button>}</div>
              {!isMd && view !== "capacity" && (
                <div>
                  <Label className="text-[11px]">Zoom</Label>
                  <Segmented size="sm" value={zoom} onChange={setZoom} options={ZOOM_ORDER.map((z) => ({ value: z, label: ZOOM[z].label, testId: `planning-zoom-${z}` }))} className="mt-1 w-full [&>button]:flex-1" />
                </div>
              )}
              {view !== "project" && <div><Label className="text-[11px]">Modultype</Label><ModuleTypeSelect value={filters.module_type_id} onChange={(v) => setFilter("module_type_id", v || "")} className="h-8" testId="planning-type-filter" /></div>}
              {view === "module" && !focusId && <div><Label className="text-[11px]">Modulstatus</Label><EnumSelect value={filters.status || "all"} onChange={(v) => setFilter("status", v === "all" ? "" : v)} options={MODULE_STATUSES} labelFn={label} allLabel="Alle statuser" className="h-8" testId="planning-status-filter" /></div>}
              {view === "project" && <div className="text-[11.5px] text-muted-foreground">Bytt til «Moduler» for å filtrere på modultype og status.</div>}
              {view !== "capacity" && (
                <>
                  <Separator />
                  <label className="flex items-center justify-between gap-3 text-[12.5px]"><span>Kun konflikter</span><Switch checked={!!filters.conflicts} onCheckedChange={(v) => setFilter("conflicts", v ? "1" : "")} data-testid="planning-conflict-toggle" /></label>
                  <label className="flex items-center justify-between gap-3 text-[12.5px]"><span>Kapasitetslinje i header</span><Switch checked={showStrip} onCheckedChange={(v) => setFilter("strip", v ? "1" : "0")} data-testid="planning-strip-toggle" /></label>
                </>
              )}
            </PopoverContent>
          </Popover>

          {view !== "capacity" && (
            <TextBtn onClick={openConflicts} active={showRail} tone={conflictCount ? "danger" : undefined} testId="planning-conflicts-button" aria-pressed={showRail} className={cn(!conflictCount && "text-muted-foreground")}>
              {conflictCount > 0 && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" aria-hidden />}
              <span className="font-mono tabular">{conflictCount}</span>
              <span className="hidden xl:inline">konflikt{conflictCount === 1 ? "" : "er"}</span>
            </TextBtn>
          )}

          {canWrite && (
            <Button size="sm" className="h-8 gap-1 rounded-md px-2.5 text-[12.5px] shadow-none" onClick={() => setAllocate({ projectId: focusId || undefined })} data-testid="planning-new-reservation-button">
              <Plus className="h-3.5 w-3.5" /> <span className="hidden md:inline">Reserver</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" aria-label="Mer" data-testid="planning-more-button"><MoreHorizontal className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {tlView === "project" && view !== "capacity" && <><DropdownMenuItem onClick={() => expandAll(true)}>Utvid alle prosjekter</DropdownMenuItem><DropdownMenuItem onClick={() => expandAll(false)}>Fold alle</DropdownMenuItem><DropdownMenuSeparator /></>}
              <DropdownMenuItem onClick={() => setShortcutsOpen(true)} data-testid="planning-shortcuts-button">Tastatursnarveier <span className="ml-auto kbd">?</span></DropdownMenuItem>
              {active > 0 && <DropdownMenuItem onClick={reset}>Nullstill filter og periode</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ---------- workspace ---------- */}
      <div className="flex min-h-0 flex-1">
        <section className="flex min-w-0 flex-1 flex-col" data-testid="planning-workspace">
          {(focus && view !== "capacity") || activeChips.length > 0 ? (
            <div className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5 text-[12px]" data-testid="planning-context-bar">
              {focus && view !== "capacity" && (
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1" data-testid="planning-focus-banner">
                  <span className="font-mono text-[11.5px] text-muted-foreground">{focus.project_number}</span>
                  <StatusBadge status={focus.status} entity="project" className="text-[10px] py-0 ring-0 bg-muted text-foreground/80" />
                  {focus.customer_name && <span className="hidden text-muted-foreground md:inline">{focus.customer_name}</span>}
                  {focus.address && <span className="hidden text-muted-foreground xl:inline">{focus.address}</span>}
                  <span className="hidden text-muted-foreground tabular sm:inline">{fmtDate(focus.start_date, "d. MMM yy")} – {fmtDate(focus.end_date, "d. MMM yy")} · {focus.module_count} moduler{focus.conflict_count ? <span className="font-medium text-rose-700 dark:text-rose-300"> · {focus.conflict_count} konflikt{focus.conflict_count === 1 ? "" : "er"}</span> : ""}</span>
                  <div className="flex items-center gap-2 text-[12px]">
                    <button type="button" className="font-medium text-primary hover:underline" onClick={() => navigate(`/prosjekter/${focus.id}`)} data-testid="planning-focus-open-project">Åpne prosjekt</button>
                    {canWrite && <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setImpact({ project: { id: focus.id, name: focus.name, start_date: focus.start_date, end_date: focus.end_date } })} data-testid="planning-focus-move">Flytt prosjekt</button>}
                    <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setFocus("")} data-testid="planning-focus-exit">Alle prosjekter</button>
                  </div>
                </div>
              )}
              {activeChips.length > 0 && (
                <div className="ml-auto flex flex-wrap items-center gap-1.5" data-testid="planning-active-filters">
                  {activeChips.map((c) => (
                    <span key={c.k} className="inline-flex items-center gap-1 rounded-md bg-muted pl-2 pr-1 py-0.5 text-[11.5px]" data-testid={`planning-chip-${c.k}`}>
                      {c.l}
                      <button type="button" onClick={() => setFilter(c.k, "")} className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label="Fjern filter"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <div className="relative min-h-0 flex-1">
            {view === "capacity" ? (
              capLoading && !capacity ? <CapacitySkeleton /> : capError ? <ErrorState error={capError} onRetry={refetchCap} /> : (
                <CapacityView data={capacity} onCellClick={(r, b) => { setMany({ view: "module", module_type_id: r.module_type_id, project_id: "", status: "" }); pendingScroll.current = b.start; }} />
              )
            ) : isLoading && !data ? (
              <GanttSkeleton leftWidth={isDesktop ? 272 : 150} />
            ) : error ? (
              <ErrorState error={error} onRetry={refetch} />
            ) : (
              <Gantt
                ref={ganttRef}
                rows={flat}
                start={range.start}
                end={range.end}
                zoom={zoom}
                view={tlView}
                focus={!!focus}
                canWrite={canWrite}
                selectedId={selected.barId}
                selectedRowId={selected.rowId}
                onSelect={onSelect}
                onOpen={openBar}
                onToggle={toggleRow}
                onRowClick={onRowClick}
                onBarChange={onBarChange}
                onCreate={canWrite ? onCreate : undefined}
                onRangeChange={setRange}
                onVisibleRange={onVisibleRange}
                capacityTotals={stripTotals}
                renderRowActions={renderRowActions}
                leftHeader={leftHeader}
                leftWidth={isDesktop ? 272 : 150}
                emptyContent={emptyContent}
              />
            )}
          </div>

          {/* status bar */}
          <footer className="flex h-8 shrink-0 items-center gap-2 overflow-x-auto border-t px-2 text-[11.5px] text-muted-foreground scrollbar-thin sm:px-3" data-testid="planning-statusbar">
            {focus && view !== "capacity" ? (
              <>
                <Stat value={focus.module_count} label="moduler" testId="planning-kpi-focus-modules" />
                <span className="text-border">·</span>
                <Stat value={`${Math.max(0, differenceInCalendarDays(parseISO(focus.end_date), parseISO(focus.start_date)))} d`} label="prosjektperiode" testId="planning-kpi-focus-period" />
                <span className="text-border">·</span>
                <Stat value={focus.conflict_count} label={focus.conflict_count === 1 ? "konflikt" : "konflikter"} tone={focus.conflict_count ? "danger" : undefined} onClick={openConflicts} testId="planning-kpi-conflicts" />
              </>
            ) : (
              <>
                <Stat value={capNow ? `${capNow.pct} %` : "–"} label="utnyttelse nå" onClick={() => setView("capacity")} testId="planning-kpi-utilization" />
                <span className="text-border">·</span>
                <Stat value={capNow ? capNow.available : "–"} label="ledige moduler" testId="planning-kpi-available" />
                <span className="text-border">·</span>
                <Stat value={rows.length} label={tlView === "project" ? "prosjekter" : "moduler"} testId="planning-kpi-projects" />
                <span className="text-border">·</span>
                <Stat value={conflictCount} label={conflictCount === 1 ? "konflikt" : "konflikter"} tone={conflictCount ? "danger" : undefined} onClick={openConflicts} testId="planning-kpi-conflicts" />
              </>
            )}
            <div className="ml-auto flex items-center gap-3">
              {view !== "capacity" && (
                <Popover>
                  <PopoverTrigger asChild><button type="button" className="rounded px-1 text-[11.5px] text-muted-foreground hover:text-foreground" data-testid="planning-legend-button">Forklaring</button></PopoverTrigger>
                  <PopoverContent align="end" className="w-[300px] p-3" data-testid="planning-legend">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
                      {LEGEND.map(([k, l]) => <span key={k} className="inline-flex items-center gap-2"><Swatch k={k} /> {l}</span>)}
                      <span className="inline-flex items-center gap-2"><Swatch k="reservert" className="bar-planned" /> Planlagt (skravert)</span>
                      <span className="inline-flex items-center gap-2"><Swatch k="prosjekt" className="bar-other" /> Annet prosjekt</span>
                    </div>
                    <Separator className="my-3" />
                    <div className="text-[11.5px] leading-relaxed text-muted-foreground">Dra en bar for å flytte, dra kanten for å endre lengde, dra i tom rad for ny reservasjon, dobbeltklikk for detaljer. <button type="button" className="underline hover:text-foreground" onClick={() => setShortcutsOpen(true)}>Tastatursnarveier</button></div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </footer>
        </section>

        {showRail && (
          <aside className="w-[300px] shrink-0 border-l xl:w-[340px]" data-testid="planning-conflict-aside">
            <ConflictRail conflicts={conflicts} loading={isLoading && !data} canWrite={canWrite} onResolve={resolveConflict} onLocate={locateConflict} />
          </aside>
        )}
      </div>

      {/* ---------- sheets & dialogs ---------- */}
      <AllocationSheet allocationId={allocId} open={!!allocId} onOpenChange={(o) => !o && setAllocId(null)} canWrite={canWrite} onChanged={() => {}} />

      <ImpactSheet open={!!impact} onOpenChange={(o) => !o && setImpact(null)} project={impact?.project || null} initialStart={impact?.initialStart} initialEnd={impact?.initialEnd} onApplied={() => invalidateAll()} />

      <AllocateSheet open={!!allocate} onOpenChange={(o) => !o && setAllocate(null)} project={null} projectId={allocate?.projectId} preset={allocate?.preset} onDone={() => invalidateAll()} />

      <Sheet open={railSheet} onOpenChange={setRailSheet}>
        <SheetContent className="w-[420px] max-w-[94vw] p-0" data-testid="planning-conflicts-sheet">
          <ConflictRail conflicts={conflicts} canWrite={canWrite} onResolve={resolveConflict} onLocate={locateConflict} />
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!dragConflict} onOpenChange={(o) => !o && setDragConflict(null)}>
        <AlertDialogContent data-testid="planning-drag-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Flyttingen gir dobbelbooking</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>{dragConflict?.bar?.module_code} · {fmtDate(dragConflict?.next?.start)} – {fmtDate(dragConflict?.next?.end)} overlapper med:</div>
                <ul className="rounded-md border divide-y text-xs">
                  {(dragConflict?.conflicts || []).map((c) => <li key={c.id} className="flex items-center gap-2 px-3 py-1.5"><span className="text-muted-foreground">{label(c.type)}</span><span className="flex-1 truncate">{c.project_number} {c.project_name || ""}</span><span className="tabular text-muted-foreground">{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</span></li>)}
                </ul>
                <div className="text-muted-foreground">Angre flyttingen, eller lagre likevel – konflikten registreres og vises i konfliktpanelet.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="planning-drag-conflict-cancel">Angre flytting</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={forceDrag} data-testid="planning-drag-conflict-force">Lagre likevel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="max-w-md" data-testid="planning-shortcuts-dialog">
          <DialogHeader><DialogTitle className="font-display">Tastatursnarveier</DialogTitle><DialogDescription>Jobb raskere i planleggingen.</DialogDescription></DialogHeader>
          <ul className="divide-y text-sm">
            {SHORTCUTS.map(([k, d]) => <li key={k} className="flex items-center justify-between gap-4 py-2"><span className="text-muted-foreground">{d}</span><span className="kbd whitespace-nowrap">{k}</span></li>)}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
